import { Component, Inject, OnInit } from '@angular/core';
import { TuiAlertService } from '@taiga-ui/core';
import { Apollo } from 'apollo-angular';

import {
  Company,
  DiscoveredField,
  FETCH_SAP_SAMPLE_DATA_MUTATION,
  FetchSapSampleDataResponse,
  GENERATE_UNIVERSAL_TEMPLATE_MUTATION,
  GenerateUniversalTemplateInput,
  GenerateUniversalTemplateResponse,
  GET_COMPANIES_QUERY,
  GET_COMPANY_SAP_MAPPINGS_QUERY,
  GET_SAP_QUERY_TEMPLATES_QUERY,
  SapFieldMapping,
  SapQueryTemplate,
  SAVE_SAP_FIELD_MAPPINGS_MUTATION,
  SaveSapFieldMappingsInput,
  SaveSapFieldMappingsResponse,
  TEST_SAP_QUERY_TEMPLATE_MUTATION,
  TestSapQueryTemplateInput,
  TestSapQueryTemplateResponse,
} from './config-company.graphql';

interface OptionalField {
  field_name: string;
  label: string;
  description: string;
  sap_field_path: string;
  enabled: boolean;
  suggested_sap_paths: string[];
}

@Component({
  selector: 'app-config-company',
  templateUrl: './config-company.component.html',
  styleUrls: ['./config-company.component.scss'],
  standalone: false,
})
export class ConfigCompanyComponent implements OnInit {
  // State management
  currentStep = 1;
  selectedCompanyId: number | null = null;
  generatedTemplateId: number | null = null;

  // Data
  companies: Company[] = [];
  fieldMappings: SapFieldMapping[] = [];
  discoveredFields: DiscoveredField[] = [];
  existingTemplates: SapQueryTemplate[] = [];

  // Loading states
  loading = false;
  fetchingData = false;
  savingConfiguration = false;
  generatingTemplate = false;
  testingQuery = false;

  // Form data
  templateName = 'Universal Search - All Warehouses';
  includeOptionalFields = false;
  generatedSql = '';
  testResults: any = null;

  // Core field definitions
  stockCountFields = [
    { our_field: 'item_code', label: 'Item Code', required: true },
    { our_field: 'item_name', label: 'Item Name', required: true },
    { our_field: 'barcode', label: 'Primary Barcode', required: false },
    { our_field: 'system_quantity', label: 'System Quantity', required: true },
    { our_field: 'bin_location', label: 'Bin Location', required: false },
    {
      our_field: 'additional_barcodes',
      label: 'Additional Barcodes',
      required: false,
    },
    { our_field: 'unit_of_measure', label: 'Unit of Measure', required: false },
    {
      our_field: 'primary_identifier',
      label: 'Primary Identifier',
      required: false,
    },
  ];

  // Array strategy options
  arrayStrategies = [
    { value: 'first', label: 'Take First' },
    { value: 'all', label: 'Take All (Array)' },
    { value: 'warehouse_specific', label: 'Warehouse Specific' },
    { value: 'filter', label: 'Apply Filter' },
  ];

  // Optional generic fields
  optionalFields: OptionalField[] = [
    {
      field_name: 'manufacturer',
      label: 'Manufacturer',
      description: 'Item manufacturer information',
      sap_field_path: '',
      enabled: false,
      suggested_sap_paths: ['FrgnName', 'Manufacturer', 'U_MANUFACTURER'],
    },
    {
      field_name: 'category',
      label: 'Item Category',
      description: 'Product category or group',
      sap_field_path: '',
      enabled: false,
      suggested_sap_paths: ['ItmsGrpCod', 'QryGroup1', 'U_CATEGORY'],
    },
    {
      field_name: 'cost_price',
      label: 'Cost Price',
      description: 'Item cost price',
      sap_field_path: '',
      enabled: false,
      suggested_sap_paths: ['LastPurPrc', 'AvgPrice', 'StdPrice'],
    },
    {
      field_name: 'sales_price',
      label: 'Sales Price',
      description: 'Item sales price',
      sap_field_path: '',
      enabled: false,
      suggested_sap_paths: ['Price', 'SalUnitMsr', 'U_SALES_PRICE'],
    },
    {
      field_name: 'supplier_code',
      label: 'Supplier Code',
      description: 'Primary supplier information',
      sap_field_path: '',
      enabled: false,
      suggested_sap_paths: ['CardCode', 'VendorNum', 'U_SUPPLIER'],
    },
  ];

  constructor(
    private apollo: Apollo,
    @Inject(TuiAlertService) private readonly alertService: TuiAlertService
  ) {}

  ngOnInit(): void {
    this.loadCompanies();
    this.initializeFieldMappings();
  }

  // Stringify function for company dropdown
  stringifyCompany = (companyId: number | null): string => {
    if (companyId === null || companyId === undefined) return '';

    // Convert to string for comparison since GraphQL returns strings
    const idToFind = companyId.toString();

    const company = this.companies.find(c => c.id.toString() === idToFind);
    return company ? company.name : '';
  };

  // Step navigation
  showStep(step: number): void {
    this.currentStep = step;
  }

  // Data loading
  async loadCompanies(): Promise<void> {
    try {
      this.loading = true;

      const result = await this.apollo
        .query<{ Companies: { results: Company[]; totalCount: number } }>({
          query: GET_COMPANIES_QUERY,
        })
        .toPromise();

      if (result?.data?.Companies?.results) {
        this.companies = result.data.Companies.results;
      }
    } catch (error) {
      this.showAlert('Error loading companies', 'error');
      console.error('Error loading companies:', error);
    } finally {
      this.loading = false;
    }
  }

  async loadCompanyData(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.showAlert('Please select a company first', 'error');
      return;
    }

    try {
      this.fetchingData = true;

      // Ensure companyId is a number
      const companyId =
        typeof this.selectedCompanyId === 'string'
          ? parseInt(this.selectedCompanyId, 10)
          : this.selectedCompanyId;

      // Load field mappings
      const mappingsResult = await this.apollo
        .query<{ sapFieldMappings: SapFieldMapping[] }>({
          query: GET_COMPANY_SAP_MAPPINGS_QUERY,
          variables: { companyId },
        })
        .toPromise();

      // Load existing templates
      const templatesResult = await this.apollo
        .query<{ odataTemplates: SapQueryTemplate[] }>({
          query: GET_SAP_QUERY_TEMPLATES_QUERY,
          variables: { companyId },
        })
        .toPromise();

      if (mappingsResult?.data?.sapFieldMappings) {
        this.fieldMappings = mappingsResult.data.sapFieldMappings || [];
        this.populateFieldMappings();
      }

      if (templatesResult?.data?.odataTemplates) {
        this.existingTemplates = templatesResult.data.odataTemplates;
      }

      const selectedCompany = this.companies.find(
        c => c.id.toString() === this.selectedCompanyId.toString()
      );
      this.showAlert(
        `Company data loaded successfully for ${selectedCompany?.name}`,
        'success'
      );
    } catch (error) {
      this.showAlert('Error loading company data', 'error');
      console.error('Error loading company data:', error);
    } finally {
      this.fetchingData = false;
    }
  }

  async fetchSampleData(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.showAlert('Please select a company first', 'error');
      return;
    }

    try {
      this.fetchingData = true;
      this.showAlert('Fetching sample SAP data...', 'info');

      const result = await this.apollo
        .mutate<{ fetchSapSampleData: FetchSapSampleDataResponse }>({
          mutation: FETCH_SAP_SAMPLE_DATA_MUTATION,
          variables: { companyId: this.selectedCompanyId.toString() },
        })
        .toPromise();

      const response = result?.data?.fetchSapSampleData;
      if (response?.success) {
        this.discoveredFields = response.discoveredFields || [];
        this.populateSAPFieldDropdowns();
        this.showAlert(
          `Sample data fetched successfully for item: ${response.itemCode}`,
          'success'
        );
      } else {
        this.showAlert(
          `Error fetching sample data: ${response?.error}`,
          'error'
        );
      }
    } catch (error) {
      this.showAlert('Error fetching sample data', 'error');
      console.error('Error fetching sample data:', error);
    } finally {
      this.fetchingData = false;
    }
  }

  async saveConfiguration(): Promise<void> {
    if (!this.selectedCompanyId) {
      this.showAlert('Please select a company first', 'error');
      return;
    }

    try {
      this.savingConfiguration = true;

      const enabledOptionalFields = this.optionalFields.filter(
        f => f.enabled && f.sap_field_path
      );

      const input: SaveSapFieldMappingsInput = {
        companyId: this.selectedCompanyId.toString(),
        fieldMappings: this.fieldMappings.map(fm => ({
          ourField: fm.ourField,
          sapFieldPath: fm.sapFieldPath,
          arrayStrategy: fm.arrayStrategy,
          includeInStorage: fm.includeInStorage,
        })),
        optionalFields: enabledOptionalFields.map(of => ({
          fieldName: of.field_name,
          label: of.label,
          description: of.description,
          sapFieldPath: of.sap_field_path,
          enabled: of.enabled,
        })),
      };

      const result = await this.apollo
        .mutate<{ saveSapFieldMappings: SaveSapFieldMappingsResponse }>({
          mutation: SAVE_SAP_FIELD_MAPPINGS_MUTATION,
          variables: { input },
        })
        .toPromise();

      const response = result?.data?.saveSapFieldMappings;
      if (response?.success) {
        this.showAlert(response.message, 'success');
        if (response.mappings) {
          this.fieldMappings = response.mappings;
        }
      } else {
        this.showAlert(
          `Error saving configuration: ${response?.error}`,
          'error'
        );
      }
    } catch (error) {
      this.showAlert('Error saving configuration', 'error');
      console.error('Error saving configuration:', error);
    } finally {
      this.savingConfiguration = false;
    }
  }

  async generateTemplate(): Promise<void> {
    if (!this.selectedCompanyId || !this.templateName.trim()) {
      this.showAlert(
        'Please select a company and enter a template name',
        'error'
      );
      return;
    }

    try {
      this.generatingTemplate = true;
      this.showAlert('Generating universal search template...', 'info');

      const input: GenerateUniversalTemplateInput = {
        companyId: this.selectedCompanyId.toString(),
        templateName: this.templateName,
        includeOptionalFields: this.includeOptionalFields,
        description:
          'Universal search template with OR conditions for itemcode, barcodes, and primary identifier across all warehouses',
      };

      const result = await this.apollo
        .mutate<{
          generateUniversalTemplate: GenerateUniversalTemplateResponse;
        }>({
          mutation: GENERATE_UNIVERSAL_TEMPLATE_MUTATION,
          variables: { input },
        })
        .toPromise();

      const response = result?.data?.generateUniversalTemplate;
      if (response?.success && response.template) {
        this.generatedTemplateId = parseInt(response.template.id);
        this.generatedSql = response.template.sqlText;
        this.showAlert(response.message, 'success');
      } else {
        this.showAlert(
          `Error generating template: ${response?.error}`,
          'error'
        );
      }
    } catch (error) {
      this.showAlert('Error generating template', 'error');
      console.error('Error generating template:', error);
    } finally {
      this.generatingTemplate = false;
    }
  }

  async testQuery(): Promise<void> {
    if (!this.generatedTemplateId) {
      this.showAlert('Please generate a template first', 'error');
      return;
    }

    try {
      this.testingQuery = true;
      this.showAlert('Testing template...', 'info');

      const input: TestSapQueryTemplateInput = {
        companyId: this.selectedCompanyId.toString(),
        templateId: this.generatedTemplateId.toString(),
        warehouseCode: '01',
        searchTerm: 'TEST',
        limit: 10,
      };

      const result = await this.apollo
        .mutate<{ testSapQueryTemplate: TestSapQueryTemplateResponse }>({
          mutation: TEST_SAP_QUERY_TEMPLATE_MUTATION,
          variables: { input },
        })
        .toPromise();

      const response = result?.data?.testSapQueryTemplate;
      if (response?.success) {
        this.testResults = response;
        this.showAlert('Template test completed successfully', 'success');
      } else {
        this.showAlert(`Template test failed: ${response?.error}`, 'error');
      }
    } catch (error) {
      this.showAlert('Error testing template', 'error');
      console.error('Error testing template:', error);
    } finally {
      this.testingQuery = false;
    }
  }

  // Helper methods
  private initializeFieldMappings(): void {
    this.fieldMappings = this.stockCountFields.map(field => ({
      id: '',
      ourField: field.our_field,
      sapFieldPath: '',
      arrayStrategy: 'first',
      includeInStorage: true,
    }));
  }

  private populateFieldMappings(): void {
    // This would populate existing field mappings from the server
    // Implementation depends on your data structure
  }

  private populateSAPFieldDropdowns(): void {
    // Extract field paths from discovered fields for dropdowns
    // Implementation depends on your field structure
  }

  showAlert(
    message: string,
    type: 'success' | 'error' | 'info' = 'info'
  ): void {
    const appearance =
      type === 'error' ? 'error' : type === 'success' ? 'success' : 'info';
    this.alertService
      .open(message, {
        label:
          type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Info',
        appearance,
      })
      .subscribe();
  }

  // Template getters for UI
  get progressPercentage(): number {
    return (this.currentStep / 4) * 100;
  }

  get canProceedToStep2(): boolean {
    return this.selectedCompanyId !== null;
  }

  get canProceedToStep3(): boolean {
    return this.fieldMappings.some(m => m.sapFieldPath);
  }

  get canProceedToStep4(): boolean {
    return this.generatedTemplateId !== null;
  }
}
